import type {
  ChatData,
  ChatMessage,
  ChatParticipant,
  CleanedData,
  ReportData,
  UserContextData,
} from "../types/global.d";
import type { UserData } from "../api/auth";
import { decodeHtmlEntities } from "./htmlEntities";

type UserTextFields = {
  fullname?: string;
};

const decodeText = <T extends string | undefined | null>(value: T): T => {
  return (typeof value === "string" ? decodeHtmlEntities(value) : value) as T;
};

const decodeLocationName = <T extends string | undefined | null>(value: T): T => {
  const decoded = decodeText(value);

  if (typeof decoded !== "string") {
    return decoded;
  }

  const trimmed = decoded.trim();
  if (!trimmed.startsWith('"') || !trimmed.endsWith('"')) {
    return decoded as T;
  }

  try {
    const parsed = JSON.parse(trimmed);
    return (typeof parsed === "string" ? parsed : decoded) as T;
  } catch {
    return decoded as T;
  }
};

export const normalizeUserTextFields = <T extends UserTextFields | null | undefined>(
  user: T,
): T => {
  if (!user) {
    return user;
  }

  return {
    ...user,
    fullname: decodeText(user.fullname),
  } as T;
};

export const normalizeUserDataTextFields = <T extends UserData | UserContextData>(
  user: T,
): T => normalizeUserTextFields(user) as T;

const normalizeCleanedTextFields = (cleaned?: CleanedData): CleanedData | undefined => {
  if (!cleaned) {
    return cleaned;
  }

  return {
    ...cleaned,
    comment: decodeText(cleaned.comment),
    user: normalizeUserTextFields(cleaned.user),
  };
};

export const normalizeReportTextFields = (report: ReportData): ReportData => {
  return {
    ...report,
    comment: decodeText(report.comment),
    locationName: decodeLocationName(report.locationName),
    user: normalizeUserTextFields(report.user),
    cleaned: normalizeCleanedTextFields(report.cleaned),
  };
};

export const normalizeReportsTextFields = (reports: ReportData[]): ReportData[] =>
  reports.map(normalizeReportTextFields);

const normalizeChatParticipantTextFields = (
  participant: ChatParticipant,
): ChatParticipant => normalizeUserTextFields(participant);

export const normalizeChatMessageTextFields = (
  message: ChatMessage,
): ChatMessage => ({
  ...message,
  content:
    message.messageType === "text"
      ? decodeHtmlEntities(message.content)
      : message.content,
  sender: normalizeChatParticipantTextFields(message.sender),
});

export const normalizeChatTextFields = (chat: ChatData): ChatData => ({
  ...chat,
  participants: (chat.participants || []).map(normalizeChatParticipantTextFields),
  messages: (chat.messages || []).map(normalizeChatMessageTextFields),
  lastMessagePreview: chat.lastMessagePreview
    ? normalizeChatMessageTextFields(chat.lastMessagePreview)
    : chat.lastMessagePreview,
});

export const normalizeChatsTextFields = (chats: ChatData[]): ChatData[] =>
  chats.map(normalizeChatTextFields);
